import type BodlEvents from '@bigcommerce/bodl-events';
import { v4 as uuidv4 } from 'uuid';

import { getCookieValue } from '../client-cookies';

import { subscribeOnBodlEvents } from './providers/ga4/google_analytics4';

declare global {
  interface Window {
    bodlEvents?: typeof BodlEvents;
  }
}

interface BodlGoogleAnalyticsConfig {
  id?: string;
  consentModeEnabled?: boolean;
  developerId?: string;
}

interface BodlConfig {
  channelId: number;
  googleAnalytics: BodlGoogleAnalyticsConfig;
  googleTagManager: {
    id?: string;
  };
}

export class Bodl {
  static #instance: Bodl | null = null;

  readonly cart = this.getCartEvents();
  readonly navigation = this.getNavigationEvents();
  readonly consent = this.getConsentEvents();

  private readonly bodlScriptId = 'bodl-events-script';
  private readonly dataLayerScriptId = 'data-layer-script';
  private readonly gtagScriptId = 'gtag-script';

  constructor(private config: BodlConfig) {
    if (Bodl.#instance) {
      return Bodl.#instance;
    }

    Bodl.#instance = this;
  }

  static waitForBodlEvents(callback: () => void, iteration = 0) {
    if (window.bodlEvents) {
      callback();

      return;
    }

    if (iteration >= 10) {
      return;
    }

    setTimeout(() => {
      this.waitForBodlEvents(callback, iteration + 1);
    }, 1000);
  }

  initialize() {
    try {
      this.assertsValidConfig(this.config);

      if (typeof window === 'undefined') {
        throw new Error('Bodl is only available in the browser environment');
      }

      this.initializeConsentMode();
      this.initializeBodlEvents();
      this.initializeGTM();
      this.initializeOneTrust();

      this.bindEvents();

      Bodl.waitForBodlEvents(() => {
        subscribeOnBodlEvents(
          this.config.googleAnalytics.id,
          this.config.googleAnalytics.consentModeEnabled,
        );
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(error);
    }
  }

  private assertsValidConfig(config?: BodlConfig): asserts config is BodlConfig {
    if (!this.config.channelId) {
      throw new Error('Bodl requires a channel ID');
    }
  }

  private initializeBodlEvents() {
    const existingScript = document.getElementById(this.bodlScriptId);

    if (existingScript) {
      return;
    }

    const script = document.createElement('script');

    script.id = this.bodlScriptId;
    script.type = 'text/javascript';
    script.src = 'https://microapps.bigcommerce.com/bodl-events/index.js';

    document.body.appendChild(script);
  }

  private initializeDataLayer() {
    const existingScript = document.getElementById(this.dataLayerScriptId);

    if (existingScript) {
      return;
    }

    const script = document.createElement('script');

    script.id = this.dataLayerScriptId;
    script.type = 'text/javascript';
    script.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        dataLayer.push(arguments);
      }
      gtag('js', new Date());
    `;

    document.body.appendChild(script);
  }

  private initializeGTM() {
    if (!this.config.googleTagManager.id) {
      return;
    }

    const existingScript = document.getElementById(this.gtagScriptId);

    if (existingScript) {
      return;
    }

    const script = document.createElement('script');

    script.id = this.gtagScriptId;
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${this.config.googleTagManager.id}');`;

    document.head.appendChild(script);
  }

  private initializeOneTrust() {
    const cookie = getCookieValue('OptanonConsent');

    if (!cookie) {
      return;
    }

    // push 'OptanonConsent' groups from cookie to dataLayer, if exists
    const match = /groups[^&]*?[=,]([\d_:,]*)?/.exec(cookie);

    if (match?.[1]) {
      // replace any sub-group (v4 cookie) with 2 or more digits and/or _, and the :\d correspondig part, main groups followed by :0 (disabled) and the :1 parts which will leave us with just the selected main groups
      // format the result above to the datalayer format for OnetrustActiveGroups: ,group,group,group,
      const activeGroups = `,${match[1]
        .replace(/([\d_]{2,}:\d|\d:0|:1),?/g, '')
        .split('')
        .join(',')},`;

      // @ts-expect-error it will be defined
      window.dataLayer.push({ OnetrustActiveGroups: activeGroups });
    }

    gtag('event', 'force_update_consent');
  }

  private initializeConsentMode() {
    if (!this.config.googleAnalytics.consentModeEnabled) {
      return;
    }

    gtag('consent', 'default', {
      ad_personalization: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'denied',
    });
  }

  private bindEvents() {
    this.bindConsentEvents();
  }

  private getCartEvents() {
    return {
      productAdded: (payload) => {
        Bodl.waitForBodlEvents(() => {
          window.bodlEvents?.cart.emit('bodl_v1_cart_product_added', {
            event_id: uuidv4(),
            channel_id: this.config.channelId,
            ...payload,
          });
        });
      },
      productRemoved: (payload) => {
        Bodl.waitForBodlEvents(() => {
          window.bodlEvents?.cart.emit('bodl_v1_cart_product_removed', {
            event_id: uuidv4(),
            channel_id: this.config.channelId,
            ...payload,
          });
        });
      },
      cartViewed: (payload) => {
        Bodl.waitForBodlEvents(() => {
          window.bodlEvents?.cart.emit('bodl_v1_cart_viewed', {
            event_id: uuidv4(),
            channel_id: this.config.channelId,
            ...payload,
          });
        });
      },
    } satisfies BodlAnalytics.Cart.Events;
  }

  private getNavigationEvents() {
    return {
      productViewed: (payload) => {
        Bodl.waitForBodlEvents(() => {
          window.bodlEvents?.product.emit('bodl_v1_product_page_viewed', {
            event_id: uuidv4(),
            channel_id: this.config.channelId,
            ...payload,
          });
        });
      },
      categoryViewed: (payload) => {
        Bodl.waitForBodlEvents(() => {
          window.bodlEvents?.product.emit('bodl_v1_product_category_viewed', {
            event_id: uuidv4(),
            channel_id: this.config.channelId,
            ...payload,
          });
        });
      },
    } satisfies BodlAnalytics.Navigation.Events;
  }

  private getConsentEvents() {
    return {
      consentLoaded: (payload) => {
        Bodl.waitForBodlEvents(() => {
          window.bodlEvents?.consent.emit('bodl_v1_consent_loaded', {
            event_id: uuidv4(),
            ...payload,
          });
        });
      },
      consentUpdated: (payload) => {
        Bodl.waitForBodlEvents(() => {
          window.bodlEvents?.consent.emit('bodl_v1_consent_updated', {
            event_id: uuidv4(),
            ...payload,
          });
        });
      },
    } satisfies BodlAnalytics.Consent.Events;
  }

  private bindConsentEvents() {
    Bodl.waitForBodlEvents(() => {
      window.bodlEvents?.consent.loaded((payload) => {
        gtag('consent', 'update', {
          ad_personalization: payload.advertising ? 'granted' : 'denied',
          ad_storage: payload.advertising ? 'granted' : 'denied',
          ad_user_data: payload.advertising ? 'granted' : 'denied',
          analytics_storage: payload.analytics ? 'granted' : 'denied',
          functionality_storage: payload.functional ? 'granted' : 'denied',
        });
      });

      window.bodlEvents?.consent.updated((payload) => {
        gtag('consent', 'update', {
          ad_personalization: payload.advertising ? 'granted' : 'denied',
          ad_storage: payload.advertising ? 'granted' : 'denied',
          ad_user_data: payload.advertising ? 'granted' : 'denied',
          analytics_storage: payload.analytics ? 'granted' : 'denied',
          functionality_storage: payload.functional ? 'granted' : 'denied',
        });
      });
    });
  }
}
